import type { MeetingAutomationSettings, MeetingVariableMappingEntry } from '@/services/meetingAutomationService'
import type { TemplateComponent, WhatsAppTemplate } from '@/services/whatsappService'

export type { MeetingVariableMappingEntry }

export type MeetingTimeDraft = {
  id: 'pre_visit' | 'post_visit'
  name: string
  enabled: boolean
  minutes: number
  direction: 'Before' | 'After'
  linkedTemplate: string
  templateLanguage: string
  bodyVariableCount: number
  variableMapping: MeetingVariableMappingEntry[]
}

export type MeetingTemplateAnalysis = {
  templateName: string
  compatible: boolean
  isLegacy: boolean
  reason: string | null
  bodyVariableCount: number
  bodyText: string
  status: string
  components: TemplateComponent[]
}

export const LEGACY_MEETING_TEMPLATE_NAMES = new Set(['pre_visit', 'post_visit'])

/** Canonical labels sent to the API (must match backend DEFAULT_SETTINGS). */
export const CANONICAL_MEETING_SLOT_LABELS: Record<'pre_visit' | 'post_visit', string> = {
  pre_visit: 'Previsita',
  post_visit: 'Postvisita',
}
export const CANONICAL_MEETING_VARIABLE_LABELS = [
  '{{1}} user_name',
  '{{2}} property_label',
  '{{3}} agent_name',
  '{{4}} date',
  '{{5}} time',
] as const

export type CanonicalMeetingVariableSpec = {
  slot: `{{${1 | 2 | 3 | 4 | 5}}}`
  key: 'user_name' | 'property_label' | 'agent_name' | 'date' | 'time'
  label: string
  example: string
}

export const CANONICAL_MEETING_VARIABLE_SPECS: CanonicalMeetingVariableSpec[] = [
  { slot: '{{1}}', key: 'user_name', label: 'Client name', example: 'Maria Lopez' },
  { slot: '{{2}}', key: 'property_label', label: 'Property', example: 'Calle Mayor 15' },
  { slot: '{{3}}', key: 'agent_name', label: 'Agent', example: 'Laura' },
  { slot: '{{4}}', key: 'date', label: 'Visit date', example: '25/04/2026' },
  { slot: '{{5}}', key: 'time', label: 'Visit time', example: '17:30' },
]

export type MeetingAutomationTemplatePresetId = 'pre_visit' | 'post_visit'

export const MEETING_AUTOMATION_TEMPLATE_PRESETS: Record<
  MeetingAutomationTemplatePresetId,
  {
    title: string
    suggestedName: string
    bodyText: string
    exampleValuesLine: string
  }
> = {
  pre_visit: {
    title: 'Pre-visit reminder',
    suggestedName: 'recordatorio_previsita',
    bodyText: 'Hola {{1}}, te recordamos tu visita a {{2}} con {{3}} el {{4}} a las {{5}}.',
    exampleValuesLine: CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => spec.example).join(', '),
  },
  post_visit: {
    title: 'Post-visit follow-up',
    suggestedName: 'seguimiento_postvisita',
    bodyText: 'Hola {{1}}, gracias por visitar {{2}} con {{3}}. Si te surge cualquier duda, responde a este mensaje.',
    exampleValuesLine: CANONICAL_MEETING_VARIABLE_SPECS.slice(0, 3).map((spec) => spec.example).join(', '),
  },
}

export function buildDefaultMeetingVariableMapping(count: number): MeetingVariableMappingEntry[] {
  return CANONICAL_MEETING_VARIABLE_SPECS.slice(0, count).map((spec, i) => ({
    position: i + 1,
    source_type: 'meeting' as const,
    source_key: spec.key,
  }))
}

export function buildMeetingAutomationExampleValuesLine(variableCount = CANONICAL_MEETING_VARIABLE_SPECS.length): string {
  return CANONICAL_MEETING_VARIABLE_SPECS.slice(0, variableCount)
    .map((spec) => spec.example)
    .join(', ')
}

const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g

function extractBodyText(components: TemplateComponent[]): string {
  const body = components.find((component) => component.type?.toUpperCase() === 'BODY')
  return body?.text?.trim() ?? ''
}

function hasBodyComponent(components: TemplateComponent[]): boolean {
  return components.some((component) => component.type?.toUpperCase() === 'BODY')
}

function componentContainsPlaceholders(value: unknown): boolean {
  if (typeof value === 'string') {
    return /\{\{\d+\}\}/.test(value)
  }
  if (Array.isArray(value)) {
    return value.some((item) => componentContainsPlaceholders(item))
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) => componentContainsPlaceholders(item))
  }
  return false
}

function analyzeBodyPlaceholders(bodyText: string): { compatible: boolean; bodyVariableCount: number; reason: string | null } {
  if (!bodyText) {
    return { compatible: true, bodyVariableCount: 0, reason: null }
  }

  const matches = Array.from(bodyText.matchAll(PLACEHOLDER_RE)).map((match) => Number.parseInt(match[1], 10))
  if (matches.length === 0) {
    return { compatible: true, bodyVariableCount: 0, reason: null }
  }

  const distinct = Array.from(new Set(matches)).sort((a, b) => a - b)
  const maxIndex = distinct[distinct.length - 1] ?? 0
  if (maxIndex > CANONICAL_MEETING_VARIABLE_LABELS.length) {
    return {
      compatible: false,
      bodyVariableCount: 0,
      reason: `Supports at most ${CANONICAL_MEETING_VARIABLE_LABELS.length} body variables in V1.`,
    }
  }

  const expected = Array.from({ length: maxIndex }, (_, index) => index + 1)
  const compatible = expected.length === distinct.length && expected.every((value, index) => distinct[index] === value)
  if (!compatible) {
    return {
      compatible: false,
      bodyVariableCount: 0,
      reason: 'Body variables must be consecutive Meta placeholders starting at {{1}}.',
    }
  }

  return { compatible: true, bodyVariableCount: maxIndex, reason: null }
}

export function analyzeMeetingTemplate(template: WhatsAppTemplate | undefined, templateName: string): MeetingTemplateAnalysis {
  if (LEGACY_MEETING_TEMPLATE_NAMES.has(templateName)) {
    return {
      templateName,
      compatible: true,
      isLegacy: true,
      reason: null,
      bodyVariableCount: 0,
      bodyText: '',
      status: 'LEGACY',
      components: [],
    }
  }

  if (!template) {
    return {
      templateName,
      compatible: false,
      isLegacy: false,
      reason: 'Template metadata is not available. Reload WhatsApp templates before saving.',
      bodyVariableCount: 0,
      bodyText: '',
      status: 'UNKNOWN',
      components: [],
    }
  }

  const status = template.status?.toUpperCase() || 'UNKNOWN'
  if (status !== 'APPROVED') {
    return {
      templateName: template.name,
      compatible: false,
      isLegacy: false,
      reason: 'Template must be approved before it can be used in automation.',
      bodyVariableCount: 0,
      bodyText: extractBodyText(template.components),
      status,
      components: template.components,
    }
  }

  if (!hasBodyComponent(template.components)) {
    return {
      templateName: template.name,
      compatible: false,
      isLegacy: false,
      reason: 'Templates must include a BODY component for V1 meeting automation.',
      bodyVariableCount: 0,
      bodyText: '',
      status,
      components: template.components,
    }
  }

  const hasParameterizedHeader = template.components.some((component) => {
    return component.type?.toUpperCase() === 'HEADER' && componentContainsPlaceholders(component)
  })
  if (hasParameterizedHeader) {
    return {
      templateName: template.name,
      compatible: false,
      isLegacy: false,
      reason: 'Parameterized headers are not supported in V1.',
      bodyVariableCount: 0,
      bodyText: extractBodyText(template.components),
      status,
      components: template.components,
    }
  }

  const hasParameterizedButtons = template.components.some((component) => {
    const type = component.type?.toUpperCase()
    return (type === 'BUTTON' || type === 'BUTTONS') && componentContainsPlaceholders(component)
  })
  if (hasParameterizedButtons) {
    return {
      templateName: template.name,
      compatible: false,
      isLegacy: false,
      reason: 'Parameterized buttons are not supported in V1.',
      bodyVariableCount: 0,
      bodyText: extractBodyText(template.components),
      status,
      components: template.components,
    }
  }

  const bodyText = extractBodyText(template.components)
  const bodyAnalysis = analyzeBodyPlaceholders(bodyText)
  return {
    templateName: template.name,
    compatible: bodyAnalysis.compatible,
    isLegacy: false,
    reason: bodyAnalysis.reason,
    bodyVariableCount: bodyAnalysis.bodyVariableCount,
    bodyText,
    status,
    components: template.components,
  }
}

/**
 * Selectable Meta template names for meeting automation: API order, plus any
 * non-legacy saved template name not present in the current fetch (orphan), so
 * the row can still display/save. Legacy internal names `pre_visit` / `post_visit`
 * are not listed here; the UI adds a disabled row for the active legacy value.
 */
export function buildMeetingTemplateOptionNames(
  whatsappTemplates: Pick<WhatsAppTemplate, 'name'>[],
  meetingTimesDraft: Pick<MeetingTimeDraft, 'linkedTemplate'>[]
): string[] {
  const metaNames = whatsappTemplates.map((template) => template.name)
  const seen = new Set<string>(metaNames)
  const result: string[] = [...metaNames]
  for (const mt of meetingTimesDraft) {
    const name = mt.linkedTemplate?.trim()
    if (!name || LEGACY_MEETING_TEMPLATE_NAMES.has(name)) {
      continue
    }
    if (!seen.has(name)) {
      seen.add(name)
      result.push(name)
    }
  }
  return result
}

export function buildMeetingAutomationPayload(
  meetingTimesDraft: MeetingTimeDraft[],
  whatsappTemplates: WhatsAppTemplate[]
): MeetingAutomationSettings {
  const templateByName = new Map(whatsappTemplates.map((template) => [template.name, template]))

  const buildSlot = (slotId: 'pre_visit' | 'post_visit', fallbackName: string, fallbackMinutes: number) => {
    const slot = meetingTimesDraft.find((item) => item.id === slotId)
    const templateName = slot?.linkedTemplate || fallbackName
    const analysis = analyzeMeetingTemplate(templateByName.get(templateName), templateName)
    if (!analysis.compatible) {
      throw new Error(`${templateName}: ${analysis.reason || 'Template is not compatible with V1 meeting automation.'}`)
    }

    const template = templateByName.get(templateName)
    const varCount = analysis.bodyVariableCount
    const mapping =
      slot?.variableMapping?.length === varCount && varCount > 0
        ? slot.variableMapping
        : varCount > 0
          ? buildDefaultMeetingVariableMapping(varCount)
          : []
    return {
      label: CANONICAL_MEETING_SLOT_LABELS[slotId],
      enabled: slot?.enabled ?? true,
      offset_minutes: slot?.minutes || fallbackMinutes,
      template_name: templateName,
      template_language: template?.language || slot?.templateLanguage || 'es_ES',
      body_variable_count: varCount,
      variable_mapping: mapping.length > 0 ? mapping : undefined,
      template_status: analysis.status,
      template_body_text: analysis.bodyText,
      template_components: analysis.components,
    }
  }

  return {
    pre_visit: buildSlot('pre_visit', 'pre_visit', 30),
    post_visit: buildSlot('post_visit', 'post_visit', 120),
  }
}
